import type { PropsWithChildren, ReactElement } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import Navigation from '../Navigation'
import AppSidebar from '../AppSidebar'
import AppTopbar from '../AppTopbar'
import DataSidebar from '../DataSidebar'
import clsx from 'clsx'
import { getCurrentDirection } from '@/i18n/config'
import { useUIv2 } from '@/hooks/useUIv2'

const DATA_AREA_PATH_PREFIXES = [
  '/data-catalog',
  '/connectors',
  '/data/graph',
  '/knowledge-graph',
  '/human-expert-knowledge',
  '/interactive-pnids',
  '/assets-hierarchy',
]

interface LayoutProps extends PropsWithChildren {
  title?: string
  pageTools?: () => ReactElement
  // Talwind valid bg-* classes only
  bgColor?: string
  // Custom background style for complex gradients
  bgStyle?: React.CSSProperties
  fullScreenContent?: boolean
  welcomePage?: boolean
}

const Layout = ({
  bgColor,
  bgStyle,
  pageTools,
  children,
  title,
  fullScreenContent,
  welcomePage,
}: LayoutProps) => {
  const tools = pageTools?.()
  const isRTL = getCurrentDirection() === 'rtl'
  const isV2 = useUIv2()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  // ?embed=1 strips the chrome (sidebars, topbar, page title) so the same
  // viewer route can render inside an iframe overlay (e.g. KG preview)
  // without duplicating the surrounding app frame.
  const isEmbedded = searchParams.get('embed') === '1'
  const isDataArea = DATA_AREA_PATH_PREFIXES.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
  )

  const content = (
    <main
      className={clsx(
        'w-full flex flex-col items-center flex-1 min-h-0',
        bgColor && bgColor
      )}
      style={bgStyle}
    >
      <div
        className={clsx(
          fullScreenContent ? 'w-full' : 'max-w-7xl px-8 py-6 w-full',
          'h-full',
          welcomePage && 'mt-8'
        )}
      >
        <div
          className={clsx(
            'h-full flex flex-col',
            !fullScreenContent && 'gap-4'
          )}
        >
          <header className="flex justify-between items-center">
            {title && <h1 className={clsx(
              "text-2xl font-bold",
              isRTL ? "text-right" : "text-left"
            )}>{title}</h1>}
            {tools}
          </header>
          {children}
        </div>
      </div>
    </main>
  )

  if (isEmbedded) {
    return <div className="flex h-screen w-screen">{content}</div>
  }

  if (isV2) {
    return (
      <div className="flex h-screen">
        <AppSidebar />
        {isDataArea && <DataSidebar />}
        <div className="flex flex-1 flex-col min-w-0">
          <AppTopbar />
          {content}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col justify-between h-[100vh]">
      <Navigation />
      {content}
    </div>
  )
}

export default Layout
